import { supabase } from "@/lib/supabase";
import { recordActivity } from "@/services/activityLog";

export interface Depot {
  id: string;
  team_id: string;
  name: string;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  lat: number | null;
  lng: number | null;
  is_default: boolean;
  created_at: string;
}

export async function listDepots(): Promise<Depot[]> {
  const { data, error } = await supabase
    .from("depots")
    .select("id, team_id, name, address_street, address_city, address_state, address_zip, lat, lng, is_default, created_at")
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Depot[];
}

export interface CreateDepotInput {
  name: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  lat: number;
  lng: number;
  is_default?: boolean;
}

export async function createDepot(input: CreateDepotInput, teamId: string): Promise<Depot> {
  // Insert first (never default), then flip via the atomic RPC. The old
  // pre-clear-then-insert order left the team with NO default if the insert
  // failed after the clear.
  const { data, error } = await supabase
    .from("depots")
    .insert({
      team_id: teamId,
      name: input.name,
      address_street: input.address_street ?? null,
      address_city: input.address_city ?? null,
      address_state: input.address_state ?? null,
      address_zip: input.address_zip ?? null,
      lat: input.lat,
      lng: input.lng,
      is_default: false,
    })
    .select("*")
    .single();
  if (error) throw error;
  const depot = data as Depot;
  if (input.is_default) {
    const { error: rpcErr } = await supabase.rpc("set_default_depot", { p_depot_id: depot.id });
    if (rpcErr) throw rpcErr;
    depot.is_default = true;
  }
  void recordActivity({
    kind: "depot_create",
    target_type: "depot",
    target_id: depot.id,
    summary: `Created depot "${input.name}"${input.is_default ? " (default)" : ""}`,
  });
  return depot;
}

export async function deleteDepot(id: string): Promise<void> {
  const { error } = await supabase.from("depots").delete().eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "depot_delete",
    target_type: "depot",
    target_id: id,
    summary: "Deleted depot",
  });
}
